import React, { useRef, useState, useEffect } from "react";
import { useCreateJob, useGetSalesPerson } from "../apiCalls/jobApiCalls";
import { useGetUsers, useGetJobTypes } from "../apiCalls/userApiCalls";
import { useSelector } from "react-redux";
import { ArrowBackIos } from "@mui/icons-material";
import moment from "moment";
import Select from "react-select";

export default function PostJob() {
  const [selectedOption, setSelectedOption] = useState(null);
  const [foremanShare, setForemanShare] = useState(100);
  const [workerShares, setWorkerShares] = useState({});
  const [selectedForeman, setSelectedForeman] = useState(null);
  const titleInputElement = useRef();
  const amountInputElement = useRef();
  const typeInputElement = useRef();
  const startDateInputElement = useRef();
  const endDateInputElement = useRef();
  const collectionDateInputElement = useRef();
  const foremanInputElement = useRef();
  const salesInputElement = useRef();
  const noteInputElement = useRef();
  const collectedInputElement = useRef();

  const { currentUser } = useSelector((state) => state.userSlice) || null;
  const token = currentUser.token;
  const userId = currentUser.user._id;

  const { data: salesPersons } = useGetSalesPerson(token);
  const { data: jobTypes } = useGetJobTypes(token);
  const { data: users } = useGetUsers(token, "", "foreman", "", "", 1);

  const {
    mutate: createJobMutate,
    isLoading: isCreateJobLoading,
    isError: isCreateJobError,
    error: createJobError,
  } = useCreateJob();

  let options = [];
  if (jobTypes) {
    options = jobTypes.data.data.map((item) => {
      let title = "";
      if (item.type === "Percentage") {
        title = `${item.title} @ ${item.rate}%`;
      } else {
        title = `${item.title} @ $${item.rate} per ${item.type}`;
      }

      return {
        value: item._id,
        label: title,
      };
    });
  }

  const foremen = users?.data?.data.filter((user) => user.role === "foreman");

  // Initialize selected foreman and worker shares when foremen are loaded
  useEffect(() => {
    if (foremen && foremen.length > 0 && !selectedForeman) {
      const firstForeman = foremen[0];
      setSelectedForeman(firstForeman);
      // Set the default value in the select element
      if (foremanInputElement.current) {
        foremanInputElement.current.value = firstForeman._id;
      }
    }
  }, [foremen]);

  // Initialize worker shares when selected foreman changes
  useEffect(() => {
    if (selectedForeman?.workers) {
      const initialShares = {};
      selectedForeman.workers.forEach((worker) => {
        initialShares[worker._id] = 0;
      });
      setWorkerShares(initialShares);
    }
  }, [selectedForeman]);

  const handleSubmit = (event) => {
    event.preventDefault();

    const inputStartDate = moment(startDateInputElement.current?.value);
    const adjustedStartDate = inputStartDate.set({
      hour: 12,
      minute: 0,
      second: 0,
    });
    const utcStartDate = adjustedStartDate.utc().toDate();

    const inputEndDate = moment(endDateInputElement.current?.value);
    const adjustedEndDate = inputEndDate.set({
      hour: 12,
      minute: 0,
      second: 0,
    });
    const utcEndDate = adjustedEndDate.utc().toDate();

    const inputCollectionDate = moment(
      collectionDateInputElement.current?.value
    );
    const adjustedCollectionDate = inputCollectionDate.set({
      hour: 12,
      minute: 0,
      second: 0,
    });
    const utcCollectionDate = adjustedCollectionDate.utc().toDate();

    // Create worker split array from workerShares
    const workerSplit = Object.entries(workerShares).map(
      ([workerId, share]) => ({
        worker: workerId,
        share,
      })
    );

    const data = {
      token: token,
      foremanId: foremanInputElement.current?.value,
      title: titleInputElement.current?.value,
      amount: amountInputElement.current?.value,
      addedOn: utcStartDate,
      endDate: utcEndDate,
      collectionDate: utcCollectionDate,
      salesId: salesInputElement.current?.value,
      typeId: selectedOption?.value,
      note: noteInputElement.current?.value,
      collected: collectedInputElement.current?.value,
      foremanShare,
      split: workerSplit,
    };

    createJobMutate(data);
  };

  const handleChange = (selectedOption) => {
    setSelectedOption(selectedOption);
  };

  const handleWorkerShareChange = (workerId, value) => {
    const newWorkerShares = { ...workerShares };
    newWorkerShares[workerId] = Math.min(Math.max(0, value), 100);
    setWorkerShares(newWorkerShares);

    // Calculate total worker share
    const totalWorkerShare = Object.values(newWorkerShares).reduce(
      (sum, share) => sum + share,
      0
    );
    // Update foreman share to maintain 100% total
    setForemanShare(Math.max(0, 100 - totalWorkerShare));
  };

  const handleForemanChange = (e) => {
    const foremanId = e.target.value;
    const selectedForeman = foremen.find((f) => f._id === foremanId);
    setSelectedForeman(selectedForeman);
    // Reset worker shares when foreman changes
    setWorkerShares({});
    setForemanShare(100);
  };

  return (
    <>
      <div className="flex w-full items-center justify-center">
        <div className="mx-6 md:mx-32 lg:mx-44 xl:mx-64 mt-32 w-full lg:w-2/5">
          <div className="fixed lg:left-20 xl:left-32 mt-2 hidden lg:flex lg:items-center lg:justify-center items-center justify-center w-10 h-10 rounded-full bg-white shadow btn-shadow hover:shadow-s, hover:bg-stone-50 transition-colors">
            <button
              onClick={() => window.history.back()}
              className="w-full h-full rounded-full flex items-center justify-center"
            >
              <ArrowBackIos
                style={{ fontSize: 24, paddingLeft: 6, color: "gray" }}
              />
            </button>
          </div>
          <h1 className="text-xl font-medium text-center pt-10">New Job</h1>
          <form className="pb-8" onSubmit={handleSubmit}>
            <div className="pb-8 pt-8">
              <div className="space-y-3">
                <div className="space-y-2">
                  <label
                    className="text-base font-medium text-gray-500"
                    htmlFor=""
                  >
                    Title of Job
                  </label>
                  <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                    <input
                      type="text"
                      className="outline-none w-full text-base font-medium text-black placeholder-black"
                      name="title"
                      ref={titleInputElement}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    className="text-base font-medium text-gray-500"
                    htmlFor=""
                  >
                    Foreman
                  </label>
                  <div className="rounded-xl w-full py-4 px-5 flex gap-2 border border-black">
                    <select
                      name="foremanIds"
                      className="w-full outline-none"
                      ref={foremanInputElement}
                      onChange={handleForemanChange}
                    >
                      {foremen?.map((worker) => (
                        <option key={worker._id} value={worker._id}>
                          {worker.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    className="text-base font-medium text-gray-500"
                    htmlFor=""
                  >
                    Job Type
                  </label>
                  <div className="rounded-xl w-full py-2 px-3 flex gap-2 border border-black">
                    {/* <select
                      name="jobtype"
                      className="w-full outline-none"
                      ref={typeInputElement}
                    >
                      {jobTypes?.data.data.map((type) => (
                        <option key={type._id} value={type._id}>
                          {type.type === "Percentage"
                            ? `${type.title} @ ${type.rate}%`
                            : `${type.title} @ $${type.rate} per ${type.type}`}
                        </option>
                      ))}
                    </select> */}

                    <Select
                      name="jobtype"
                      className="w-full"
                      styles={{
                        control: (baseStyles, state) => ({
                          ...baseStyles,
                          border: "none",
                          boxShadow: "none",
                          fontWeight: "600",
                        }),
                      }}
                      value={selectedOption}
                      onChange={handleChange}
                      options={options}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    className="text-base font-medium text-gray-500"
                    htmlFor=""
                  >
                    Items/Amount/Area of Job
                  </label>
                  <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                    <input
                      type="number"
                      className="outline-none w-full text-base font-medium text-black placeholder-black"
                      name="amount"
                      ref={amountInputElement}
                      step=".01"
                      onWheel={(e) => e.target.blur()}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    className="text-base font-medium text-gray-500"
                    htmlFor=""
                  >
                    Sales Person
                  </label>
                  <div className="rounded-xl w-full py-4 px-5 flex gap-2 border border-black">
                    <select
                      name="workerIds"
                      className="w-full outline-none"
                      ref={salesInputElement}
                    >
                      {salesPersons?.data.data.map((worker) => (
                        <option key={worker._id} value={worker._id}>
                          {worker.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    className="text-base font-medium text-gray-500"
                    htmlFor=""
                  >
                    Start date
                  </label>
                  <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                    <input
                      type="date"
                      name="date"
                      className="w-full outline-none bg-white"
                      ref={startDateInputElement}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    className="text-base font-medium text-gray-500"
                    htmlFor=""
                  >
                    End date
                  </label>
                  <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                    <input
                      type="date"
                      name="endDate"
                      className="w-full outline-none bg-white"
                      ref={endDateInputElement}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    className="text-base font-medium text-gray-500"
                    htmlFor=""
                  >
                    Note
                  </label>
                  <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                    <input
                      type="text"
                      className="outline-none w-full text-base font-medium text-black placeholder-black"
                      name="note"
                      ref={noteInputElement}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    className="text-base font-medium text-gray-500"
                    htmlFor=""
                  >
                    Collected?
                  </label>
                  <div className="rounded-xl w-full py-4 px-5 flex gap-2 border border-black">
                    <select
                      name="workerIds"
                      className="w-full outline-none"
                      ref={collectedInputElement}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    className="text-base font-medium text-gray-500"
                    htmlFor=""
                  >
                    Collection Date
                  </label>
                  <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                    <input
                      type="date"
                      name="collectionDate"
                      className="w-full outline-none bg-white"
                      ref={collectionDateInputElement}
                    />
                  </div>
                </div>

                <h1 className="text-lg text-center font-bold">Bonus Sharing</h1>
                <div className="flex justify-between mb-2">
                  <b>Foreman share</b>
                  <b className="text-green-500">{foremanShare} %</b>
                </div>

                {selectedForeman?.workers?.map((worker) => (
                  <div
                    key={worker._id}
                    className="flex justify-between items-center mb-2"
                  >
                    <b>{worker.name}</b>
                    <div>
                      <input
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        min="0"
                        max="100"
                        value={workerShares[worker._id] || 0}
                        onChange={(e) =>
                          handleWorkerShareChange(
                            worker._id,
                            parseInt(e.target.value) || 0
                          )
                        }
                        className="border-gray-300 border rounded-lg px-2 py-1 w-14"
                      />{" "}
                      %
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="pt-4">
              <button className="px-3 py-4 w-full text-white font-medium rounded-xl bg-[#59c3c2] ">
                {isCreateJobLoading ? "...Job Starting" : "Start a New Job"}
              </button>
            </div>
            {isCreateJobError && (
              <div className="text-sm font-medium text-red-600 pt-2">
                <p>{createJobError.response.data.error}</p>
              </div>
            )}
          </form>
        </div>
      </div>
    </>
  );
}
